<template>
  <IonPage>
    <IonContent class="ion-padding login-page" :fullscreen="true">
      <div class="background-overlay"></div>
      <div class="geometric-pattern"></div>

      <div class="login-wrapper">
        <div class="branding-panel">
          <div class="brand-content">
            <div class="logo-container">
              <img :src="logoUrl" alt="EM Normandie Logo" class="university-logo" />
            </div>

            <div class="decorative-line"></div>

            <p class="university-tagline">{{ $t('auth.businessSchool') }}</p>
            <p class="university-motto">{{ $t('auth.schoolMotto') }}</p>
          </div>

          <div class="brand-decoration">
            <div class="decoration-circle decoration-circle-1"></div>
            <div class="decoration-circle decoration-circle-2"></div>
            <div class="decoration-line-horizontal"></div>
          </div>
        </div>

        <div class="login-panel">
          <div class="login-card">
            <div class="welcome-section">
              <div class="welcome-icon">
                <IonIcon :icon="schoolOutline"></IonIcon>
              </div>
              <h1 class="welcome-title">{{ $t('auth.welcomeTitle') }}</h1>
              <p class="welcome-subtitle">
                {{ $t('auth.welcomeMessage') }}
              </p>
            </div>

            <div class="separator">
              <span class="separator-line"></span>
              <span class="separator-text">{{ $t('auth.securedConnexion') }}</span>
              <span class="separator-line"></span>
            </div>

            <div class="login-button-container">
              <AuthButton />
            </div>

            <div class="login-footer">
              <p class="security-notice">
                <IonIcon :icon="shieldCheckmarkOutline"></IonIcon>
                {{ $t('auth.authentificationMicrosoft365') }}
              </p>
            </div>
          </div>

          <div class="bottom-info">
            <p>© {{ currentYear }} {{ t('auth.EMNormandieAllRightsReserved') }}</p>
          </div>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AuthButton from '@/features/common/auth/components/Button/AuthButton.vue';
import { IonPage, IonContent, IonIcon } from '@ionic/vue';
import { schoolOutline, shieldCheckmarkOutline } from 'ionicons/icons';
import logoUrl from '@/assets/images/emn-logo-transparent.svg';

const { t } = useI18n();
const currentYear = computed(() => new Date().getFullYear());
</script>

<style scoped>
.login-page {
  --background: transparent;
  position: relative;
  min-height: 100vh;
}

.background-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    var(--ion-logo-color-two) 0%,
    #0a1a4a 50%,
    var(--ion-logo-color-one) 100%
  );
  z-index: -2;
}

.geometric-pattern {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(171, 9, 38, 0.1) 0%, transparent 30%);
  z-index: -1;
}

.login-wrapper {
  display: flex;
  min-height: 100vh;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.branding-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 3rem;
}

.brand-content {
  text-align: center;
  z-index: 1;
}

.logo-container {
  margin-bottom: 2rem;
  animation: fadeInUp 0.8s ease-out;
}

.university-logo {
  width: 180px;
  height: auto;
  filter: brightness(0) invert(1);
  transition: transform 0.3s ease;
}

.university-logo:hover {
  transform: scale(1.05);
}

.decorative-line {
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #c9a227, transparent);
  margin: 1.5rem auto;
  animation: expandWidth 1s ease-out 0.3s both;
}

.university-tagline {
  font-size: 1.5rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.university-motto {
  font-size: 0.9rem;
  color: #c9a227;
  letter-spacing: 2px;
  font-weight: 500;
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.brand-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.decoration-circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  left: -100px;
  animation: pulse 8s ease-in-out infinite;
}

.decoration-circle-2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  right: -50px;
  animation: pulse 6s ease-in-out infinite 2s;
}

.decoration-line-horizontal {
  position: absolute;
  bottom: 30%;
  left: 10%;
  right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
}

.login-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.login-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  width: 100%;
  max-width: 420px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  animation: fadeInRight 0.8s ease-out;
}

.welcome-section {
  text-align: center;
  margin-bottom: 2rem;
}

.welcome-icon {
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, var(--ion-logo-color-two), var(--ion-logo-color-one));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--ion-box-shadow);
}

.welcome-icon ion-icon {
  font-size: 32px;
  color: white;
}

.welcome-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--ion-logo-color-two);
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
}

.welcome-subtitle {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.separator {
  display: flex;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
}

.separator-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
}

.separator-text {
  font-size: 0.75rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
}

.login-button-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.login-button-container :deep(.auth-button) {
  width: 100%;
  --background: linear-gradient(135deg, var(--ion-logo-color-two), #1a2a6c);
  --background-hover: linear-gradient(135deg, #0a1a4a, var(--ion-logo-color-two));
  --border-radius: 12px;
  --padding-top: 16px;
  --padding-bottom: 16px;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  border: none;
  box-shadow: var(--ion-box-shadow);
}

.login-button-container :deep(.auth-button:hover) {
  transform: translateY(-2px);
  box-shadow: var(--ion-box-shadow-hover);
}

.login-footer {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.security-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.security-notice ion-icon {
  color: #10b981;
  font-size: 1rem;
}

.bottom-info {
  margin-top: 2rem;
  text-align: center;
}

.bottom-info p {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes expandWidth {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 80px;
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.5;
  }
}

@media (max-width: 968px) {
  .login-wrapper {
    flex-direction: column;
    padding: 1rem;
    gap: 2rem;
  }

  .branding-panel {
    padding: 2rem 1rem;
    min-height: auto;
  }

  .university-logo {
    width: 120px;
  }

  .university-tagline {
    font-size: 1.2rem;
  }

  .login-panel {
    padding: 1rem;
  }

  .login-card {
    padding: 2rem;
    border-radius: 20px;
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .decoration-circle-1,
  .decoration-circle-2 {
    display: none;
  }
}

@media (max-width: 480px) {
  .login-card {
    padding: 1.5rem;
    border-radius: 16px;
  }

  .welcome-icon {
    width: 60px;
    height: 60px;
  }

  .welcome-icon ion-icon {
    font-size: 28px;
  }

  .welcome-title {
    font-size: 1.3rem;
  }

  .university-tagline {
    font-size: 1rem;
    letter-spacing: 2px;
  }

  .university-motto {
    font-size: 0.8rem;
  }

  .separator {
    margin: 1.5rem 0;
  }
}

body.classic-theme .login-card,
body.dark-theme .login-card {
  background: rgba(20, 20, 40, 0.95);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

body.classic-theme .welcome-title,
body.dark-theme .welcome-title {
  color: #ffffff;
}

body.classic-theme .welcome-subtitle,
body.dark-theme .welcome-subtitle {
  color: rgba(255, 255, 255, 0.7);
}

body.classic-theme .separator-text,
body.dark-theme .separator-text {
  color: rgba(255, 255, 255, 0.5);
}

body.classic-theme .separator-line,
body.dark-theme .separator-line {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
}

body.classic-theme .login-footer,
body.dark-theme .login-footer {
  border-top-color: rgba(255, 255, 255, 0.1);
}

body.classic-theme .security-notice,
body.dark-theme .security-notice {
  color: rgba(255, 255, 255, 0.6);
}
</style>
